import { Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { handleDownload, printPdfBlob } from "../../../downloads/utils";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import type { DownloadFormat } from "../../UIToolkit/Clickable/DownloadFormatButton";
import { DownloadFormatButton } from "../../UIToolkit/Clickable/DownloadFormatButton";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { DateRangeSelector } from "../../UIToolkit/molecules/DateRangeSelector";
import { PdfPreview } from "../../UIToolkit/Previews/PdfPreview";
import { supportsInlinePdfPreview } from "../../UIToolkit/Previews/utils";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { SCHEDULE_EXPORT_TEST_ID } from "./consts";
import { ScheduleExportTabLayout } from "./ScheduleExportTabLayout";
import { useHydratedSettings } from "./useHydratedSettings";
import { usePtoDeletedShiftsByDate } from "./usePtoDeletedShiftsByDate";
import type { useScheduleExportAnalytics } from "./useScheduleExportAnalytics";
import { useScheduleExportSettings } from "./useScheduleExportSettings";
import { useTrackPreviewLifecycle } from "./useTrackPreviewLifecycle";
import { useWeeklyPdfPreview } from "./useWeeklyPdfPreview";
import { exportableWeeks, isAvailableWeekEnd, isAvailableWeekStart, weekIndicesInRange, weekStartOf } from "./weekRange";

const FILE_DATE_FORMAT = "yyyy-MM-dd";
const DAYS_PER_WEEK = 7;

export type WeeklyExportTabProps = {
	/** False in the minimized card — a landscape page is unreadable at that width. */
	showPreview: boolean;
	/** The week currently on screen; seeds the default range. */
	weekIndex: number;
	analytics: ReturnType<typeof useScheduleExportAnalytics>;
	onDownloaded: () => void;
};

export const WeeklyExportTab: FC<WeeklyExportTabProps> = ({ showPreview, weekIndex, analytics, onDownloaded }) => {
	const { t } = useTranslation();
	const { hospitalData, chosenDepartment, isSnf, showErrorSnackbar } = useHospitalData();

	const weeks = useMemo(() => exportableWeeks(hospitalData), [hospitalData]);

	const { weekly, saveWeekly } = useScheduleExportSettings();
	// Local for responsiveness, seeded from the stored document; the save is debounced behind it.
	// Adopts the stored document once the settings query resolves — see `useHydratedSettings`.
	const [weeklySettings, setWeeklySettings] = useHydratedSettings(weekly);
	const showPtoSheet = weeklySettings.showPtoSheet;
	const setShowPtoSheet = useCallback((checked: boolean) => setWeeklySettings({ showPtoSheet: checked }), [setWeeklySettings]);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);

	// Opens on the week the user is looking at, which is almost always what they want to hand out.
	const [range, setRange] = useState<{ start: DateTime; end: DateTime } | null>(null);
	// The viewed week, or the earliest exportable one when that week carries no shift rows to date it.
	const defaultStart = (weeks.find((week) => week.weekIndex === weekIndex) ?? weeks.at(0))?.start;
	const rangeStart = range?.start ?? defaultStart;
	const rangeEnd = range?.end ?? defaultStart?.plus({ days: DAYS_PER_WEEK - 1 });

	const weekIndices = useMemo(() => weekIndicesInRange(weeks, rangeStart, rangeEnd), [weeks, rangeStart, rangeEnd]);

	const startDateStr = rangeStart?.toFormat(FILE_DATE_FORMAT) ?? "";
	const endDateStr = rangeEnd?.toFormat(FILE_DATE_FORMAT) ?? "";

	const lastNameFirst = useLastNameFirst();
	const {
		ptoShiftsByDate,
		isLoading: isLoadingPto,
		error: ptoError,
	} = usePtoDeletedShiftsByDate({
		departmentId: chosenDepartment.department_id,
		startDate: startDateStr,
		endDate: endDateStr,
		nurses: hospitalData.nurses,
		lastNameFirst,
		enabled: showPtoSheet && weekIndices.length > 0,
	});

	// Shown on screen from the start, but never reported — the one failure the old code swallowed.
	const reportedPtoError = useRef<unknown>(null);
	useEffect(() => {
		if (!ptoError) {
			reportedPtoError.current = null;
			return;
		}
		if (reportedPtoError.current === ptoError) return;
		reportedPtoError.current = ptoError;
		analytics.trackPtoFetchError(ptoError);
	}, [ptoError, analytics]);

	const handleShowPtoSheetChange = useCallback(
		(checked: boolean) => {
			setShowPtoSheet(checked);
			saveWeekly({ showPtoSheet: checked });
			analytics.trackSettingChanged("showPtoSheet", checked);
		},
		[analytics, saveWeekly, setShowPtoSheet],
	);

	const reportPreviewError = useCallback(
		(error: unknown) => {
			analytics.trackPreviewError(error);
			showErrorSnackbar(t("download.preview.error"), {}, error);
		},
		[analytics, showErrorSnackbar, t],
	);

	// Building a multi-page landscape document for a card that cannot show it is pure waste, and unlike
	// a fetch there is nothing to warm — so the build itself is gated, not just the display.
	const canPreview = showPreview && supportsInlinePdfPreview();

	const { url, getDownloadBlob, retry, isLoading, hasError } = useWeeklyPdfPreview({
		weekIndices,
		hospitalData,
		chosenDepartment,
		isSnf,
		ptoShiftsByDate,
		lastNameFirst,
		enabled: canPreview && !isLoadingPto,
		onError: reportPreviewError,
	});

	const handleRangeChange = useCallback(
		(edge: "start" | "end") => (date: DateTime) => {
			const snapped = weekStartOf(weeks, date, edge);
			if (!snapped) return;
			setRange((previous) => {
				const current = { start: previous?.start ?? rangeStart, end: previous?.end ?? rangeEnd };
				if (!current.start || !current.end) return previous;
				return edge === "start"
					? { start: snapped, end: current.end < snapped ? snapped.plus({ days: DAYS_PER_WEEK - 1 }) : current.end }
					: { start: current.start > snapped ? snapped : current.start, end: snapped.plus({ days: DAYS_PER_WEEK - 1 }) };
			});
			analytics.trackRangeChanged(edge, { selected_date: date.toFormat(FILE_DATE_FORMAT), weeks_count: weekIndices.length });
		},
		[weeks, rangeStart, rangeEnd, analytics, weekIndices.length],
	);

	const handleDownloadClick = useCallback(
		async (format: DownloadFormat) => {
			analytics.trackDownloadStart(format);
			setIsDownloading(true);
			try {
				// Always the bytes for the CURRENT inputs: the hook reuses the preview's when they still
				// describe those inputs and builds fresh when they don't, so the file can't lag the screen.
				const bytes = await getDownloadBlob();
				handleDownload(bytes, `staffing_selected_weeks_${startDateStr}_to_${endDateStr}.pdf`, "application/pdf");
				analytics.trackDownloadSuccess(format);
				onDownloaded();
			} catch (error) {
				analytics.trackDownloadError(format, error);
				showErrorSnackbar(t("download.pdf.failed"), {}, error);
			} finally {
				setIsDownloading(false);
			}
		},
		[getDownloadBlob, startDateStr, endDateStr, analytics, onDownloaded, showErrorSnackbar, t],
	);

	// Weekly's PDF comes from its own `StaffingPDF`/react-pdf pipeline (see `useWeeklyPdfPreview`), not
	// `generatePrintoutPdf` — printing hands the browser those SAME bytes rather than building anything new.
	const handlePrintClick = useCallback(async () => {
		setIsPrinting(true);
		analytics.trackPrintStart();
		try {
			const bytes = await getDownloadBlob();
			// Awaited so a failure that only surfaces once the iframe loads (a missing contentWindow, or
			// `print()` itself throwing) is actually caught here instead of being reported as a success.
			await printPdfBlob(bytes);
			analytics.trackPrintSuccess();
			onDownloaded();
		} catch (error) {
			analytics.trackPrintError(error);
			showErrorSnackbar(t("download.print.failed"), {}, error);
		} finally {
			setIsPrinting(false);
		}
	}, [getDownloadBlob, analytics, onDownloaded, showErrorSnackbar, t]);

	useTrackPreviewLifecycle(isLoading, hasError, analytics);

	const hasWeeks = weekIndices.length > 0;

	// The reason IS the disabled condition, so the button can never be dead without saying why.
	const downloadDisabledReason = !hasWeeks ? t("download.no.weeks.available") : isLoading || isLoadingPto ? t("preparing.schedule") : undefined;

	// Bound the pickers to the loaded window and enable only real week boundaries. Without this every
	// date renders selectable while only boundaries do anything, so a mis-click reads as "this week
	// can't be exported" — which is exactly how the earlier-weeks bug presented.
	const firstWeekStart = weeks.at(0)?.start;
	const lastWeekEnd = weeks.at(-1)?.start.plus({ days: DAYS_PER_WEEK - 1 });

	return (
		<ScheduleExportTabLayout
			showPreview={showPreview}
			settings={
				<>
					<DateRangeSelector
						startDate={rangeStart}
						endDate={rangeEnd}
						onStartDateChange={handleRangeChange("start")}
						onEndDateChange={handleRangeChange("end")}
						startLabel={t("download.start.date.sunday")}
						endLabel={t("download.end.date.saturday")}
						minDate={firstWeekStart}
						maxDate={lastWeekEnd}
						allowStartAfterEnd
						shouldDisabledStartDate={(date) => !isAvailableWeekStart(weeks, date)}
						shouldDisabledEndDate={(date) => !isAvailableWeekEnd(weeks, date)}
						removeBackground
						removeBorder
						startDateTestId={`${SCHEDULE_EXPORT_TEST_ID}-weekly-start-date`}
						endDateTestId={`${SCHEDULE_EXPORT_TEST_ID}-weekly-end-date`}
						padding={0}
					/>

					{/* Same wording as the monthly tab and the Daily Printout switch — one product concept. */}
					<SystemRoundedCheckbox
						data-testid={`${SCHEDULE_EXPORT_TEST_ID}-weekly-showPtoSheet`}
						checked={showPtoSheet}
						onChange={handleShowPtoSheetChange}
						label={t("printout.show.pto")}
					/>

					{ptoError && (
						<Typography variant="caption" data-testid={`${SCHEDULE_EXPORT_TEST_ID}-pto-warning`} sx={{ color: "warning.dark" }}>
							{t("download.pto.sheet.unavailable")}
						</Typography>
					)}
				</>
			}
			preview={
				<PdfPreview
					dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-weekly-preview`}
					url={url}
					title={t("download.schedule.title")}
					isLoading={isLoading || isLoadingPto}
					hasError={hasError}
					emptyLabel={hasWeeks ? undefined : t("download.no.weeks.available")}
					action={
						hasError ? (
							<SystemButton data-testid={`${SCHEDULE_EXPORT_TEST_ID}-weekly-retry`} buttonVariant="secondary" size="small" onClick={retry}>
								{t("retry")}
							</SystemButton>
						) : undefined
					}
					sx={{ flex: 1 }}
				/>
			}
			actions={
				<DownloadFormatButton
					dataTestIdPrefix={`${SCHEDULE_EXPORT_TEST_ID}-weekly`}
					formats={["pdf"]}
					onDownload={(format) => void handleDownloadClick(format)}
					isDownloading={isDownloading}
					disabled={Boolean(downloadDisabledReason)}
					disabledReason={downloadDisabledReason}
					onPrint={() => void handlePrintClick()}
					isPrinting={isPrinting}
				/>
			}
		/>
	);
};
